/**
 * Custom error classes for ZapLens
 * Provides specific error types for better error handling and user messages
 */

/**
 * Base error class for all ZapLens errors
 */
export class ZapLensError extends Error {
  constructor(
    message: string,
    public userMessage: string,
    public recoverable: boolean = true
  ) {
    super(message);
    this.name = this.constructor.name;
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/**
 * File-related errors
 */
export class FileError extends ZapLensError {
  constructor(message: string, userMessage: string, recoverable = true) {
    super(message, userMessage, recoverable);
  }
}

export class InvalidFileTypeError extends FileError {
  constructor(fileType: string) {
    super(
      `Invalid file type: ${fileType}`,
      `Please upload a JSON file (.json). You uploaded a ${fileType} file.`,
      true
    );
  }
}

export class FileTooLargeError extends FileError {
  constructor(size: number, maxSize: number) {
    const sizeMB = (size / (1024 * 1024)).toFixed(2);
    const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(2);
    super(
      `File too large: ${sizeMB}MB exceeds maximum of ${maxSizeMB}MB`,
      `This file is too large (${sizeMB}MB). Maximum size is ${maxSizeMB}MB. Try exporting a smaller workflow or contact support.`,
      true
    );
  }
}

export class FileReadError extends FileError {
  constructor(originalError?: Error) {
    super(
      `Failed to read file: ${originalError?.message || 'Unknown error'}`,
      'We couldn\'t read your file. It may be corrupted or incompletely downloaded. Try re-downloading the export from Zapier.',
      true
    );
  }
}

/**
 * JSON parsing errors
 */
export class JSONParseError extends ZapLensError {
  constructor(originalError?: Error) {
    super(
      `Invalid JSON: ${originalError?.message || 'Malformed JSON'}`,
      'This file contains invalid JSON data. Make sure you uploaded the correct Zapier export file and that it wasn\'t modified.',
      true
    );
  }
}

/**
 * Schema validation errors
 */
export class SchemaError extends ZapLensError {
  constructor(message: string, userMessage: string, recoverable = true) {
    super(message, userMessage, recoverable);
  }
}

export class UnsupportedSchemaVersionError extends SchemaError {
  constructor(version?: string) {
    super(
      `Unsupported schema version: ${version || 'unknown'}`,
      `This file uses an unsupported Zapier export format${version ? ` (${version})` : ''}. ZapLens currently supports GDPR v1 exports. Try re-exporting from Zapier or contact support.`,
      false
    );
  }
}

export class MissingRequiredFieldError extends SchemaError {
  constructor(field: string) {
    super(
      `Missing required field: ${field}`,
      `This file is missing required data (${field}). The export may be incomplete. Try downloading a fresh export from Zapier.`,
      true
    );
  }
}

export class EmptyWorkflowError extends SchemaError {
  constructor() {
    super(
      'Workflow has no nodes',
      'This workflow doesn\'t have any steps. It may be a draft or incomplete workflow. Try selecting a different workflow or uploading a different file.',
      true
    );
  }
}

export class NoWorkflowsFoundError extends SchemaError {
  constructor() {
    super(
      'No workflows found in export',
      'This file doesn\'t contain any Zapier workflows. Make sure you uploaded the correct GDPR export file from Zapier.',
      true
    );
  }
}

/**
 * Workflow parsing errors
 */
export class WorkflowParseError extends ZapLensError {
  constructor(message: string, userMessage: string, recoverable = true) {
    super(message, userMessage, recoverable);
  }
}

export class InvalidNodeStructureError extends WorkflowParseError {
  constructor(nodeId: string, details?: string) {
    super(
      `Invalid node structure: ${nodeId}${details ? ` - ${details}` : ''}`,
      'One or more steps in this workflow have an invalid structure. The workflow may have been corrupted during export.',
      false
    );
  }
}

export class CyclicDependencyError extends WorkflowParseError {
  constructor() {
    super(
      'Workflow contains cyclic dependencies',
      'This workflow has circular step dependencies which cannot be visualized. The workflow structure may be corrupted.',
      false
    );
  }
}

/**
 * License validation errors
 */
export class LicenseError extends ZapLensError {
  constructor(message: string, userMessage: string, recoverable = true) {
    super(message, userMessage, recoverable);
  }
}

export class InvalidLicenseKeyError extends LicenseError {
  constructor() {
    super(
      'Invalid license key format',
      'This license key is not valid. Please check that you entered it correctly.',
      true
    );
  }
}

export class LicenseValidationFailedError extends LicenseError {
  constructor(reason?: string) {
    super(
      `License validation failed: ${reason || 'unknown'}`,
      `We couldn't validate your license key. ${reason || 'Please try again or contact support.'}`,
      true
    );
  }
}

export class NetworkError extends LicenseError {
  constructor(originalError?: Error) {
    super(
      `Network error: ${originalError?.message || 'Connection failed'}`,
      'Connection failed. Please check your internet connection and try again.',
      true
    );
  }
}

/**
 * PDF export errors
 */
export class PDFExportError extends ZapLensError {
  constructor(reason?: string) {
    super(
      `PDF export failed: ${reason || 'unknown error'}`,
      `We couldn't generate your PDF. ${reason || 'Please try again or contact support.'}`,
      true
    );
  }
}

/**
 * Helper function to determine if an error is recoverable
 */
export function isRecoverableError(error: unknown): boolean {
  if (error instanceof ZapLensError) {
    return error.recoverable;
  }
  // Unknown errors are considered recoverable (allow retry)
  return true;
}

/**
 * Helper function to get user-friendly error message
 */
export function getUserErrorMessage(error: unknown): string {
  if (error instanceof ZapLensError) {
    return error.userMessage;
  }

  if (error instanceof Error) {
    return `An unexpected error occurred: ${error.message}`;
  }

  return 'An unexpected error occurred. Please try again or contact support.';
}

/**
 * Helper function to log errors (can be extended for error tracking services)
 */
export function logError(error: unknown, context?: string): void {
  if (import.meta.env.DEV) {
    console.error(`[ZapLens Error${context ? ` - ${context}` : ''}]:`, error);
  }

  // TODO: Send to error tracking service (Sentry) in production
  // if (import.meta.env.PROD && window.Sentry) {
  //   window.Sentry.captureException(error, {
  //     tags: { context },
  //   });
  // }
}
